import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import localeEn150 from '@angular/common/locales/en-150';
import { ApplicationConfig, ENVIRONMENT_INITIALIZER, Provider, importProvidersFrom, inject } from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import { provideRouter, withComponentInputBinding, withHashLocation, withNavigationErrorHandler, withRouterConfig } from '@angular/router';
import { FormlyModule } from '@ngx-formly/core';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { ToastrModule } from 'ngx-toastr';
import {
  RktFormCheckboxGroupComponent,
  RktFormDatepickerComponent,
  RktFormFieldWrapperComponent,
  RktFormInputCardComponent,
  RktFormInputComponent,
  RktFormInputPasswordComponent,
  RktFormInputPhoneComponent,
  RktFormInputPlaceComponent,
  RktFormInputSecureComponent,
  RktFormRadioGroupComponent,
  RktFormRepeatComponent,
  RktFormRepeatSectionComponent,
  RktFormSelectComponent,
  RktFormSelectCountryComponent,
  RktFormSelectGroupComponent,
  RktFormSelectMultiComponent,
  RktFormSelectStateComponent,
  RktFormTextareaComponent,
  RktFormTimepickerComponent,
  RktFormlyFieldGroupComponent,
  cardNumberValidator,
  countryCodeValidator,
  minLengthValidationMessage,
  phoneNumberValidator,
  requiredValidationMessage,
  stateCodeValidator,
} from '@rocketfinancialcorp/rocket-ui/form';
import {
  RktIconRegistryService,
  accounting,
  arrowLeft,
  arrowRight,
  bank,
  beneficiary,
  businessman,
  calendar,
  card,
  check,
  checkboxOff,
  checkboxOn,
  chevronDown,
  chevronLeft,
  chevronRight,
  chevronUp,
  close,
  company,
  document,
  download,
  driverLicense,
  identificationChecked,
  info,
  monitor,
  moveMoney,
  passport,
  plus,
  schedule,
  times,
  upload,
  user,
  users,
  viewFile,
} from '@rocketfinancialcorp/rocket-ui/icon';

import { AddressRepeatSectionComponent } from '@shared/components/addresses/address-repeat-section/address-repeat-section.component';
import { CharacterCountWrapperComponent } from '@shared/components/character-count-wrapper/character-count-wrapper.component';
import { FormSectionWrapperComponent } from '@shared/components/form-section/form-section-wrapper.component';
import { maxLengthValidationMessage, patternValidationMessage } from '@shared/constants/validation-messages';
import { ValidUUIDGuard } from '@shared/guards/valid-uuid.guard';
import { ErrorInterceptor } from '@shared/interceptors/error.interceptor';
import {
  APP_ENV_CONFIG,
  ChunkLoadRecoveryService,
  captureChunkLoadRecovery,
  getEnvironment,
  handleChunkNavigationError,
  sentryProviders,
} from '@shared/services';
import { typographicQuotesExtension } from '@shared/utils';

import { APP_ROUTES } from './app.routes';

// Display locale for EU workflows (KOR-15165); LOCALE_ID stays en-US because the region is only known after the workflow loads.
registerLocaleData(localeEn150, 'en-150');

interface RktIcon<T> {
  name: T;
  data: string;
}

// KOR-15208: the outline edit and delete icons of the saved-address card (Figma 8280:8012); rocket-ui's are solid
const editOutline: RktIcon<'edit-outline'> = {
  name: 'edit-outline',
  data: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13.5 3.5a2.12 2.12 0 0 1 3 3L7 16l-4 1 1-4 9.5-9.5Zm-2 2 3 3"/></svg>`,
};

const trashOutline: RktIcon<'trash-outline'> = {
  name: 'trash-outline',
  data: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3.5 5.5h13M8 5.5V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1.5m3 0-.7 10.1a1.5 1.5 0 0 1-1.5 1.4H7.2a1.5 1.5 0 0 1-1.5-1.4L5 5.5m3.5 3v5m3-5v5"/></svg>`,
};

const camera: RktIcon<'camera'> = {
  name: 'camera',
  data: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 21 21"><path fill="currentColor" d="M10.16 3a.835.835 0 0 0-.613.27L8.512 4.396a.832.832 0 0 1-.614.27H3.831c-.917 0-1.667.75-1.667 1.667v10c0 .917.75 1.667 1.667 1.667h13.333c.917 0 1.667-.75 1.667-1.667v-10c0-.916-.75-1.666-1.667-1.666h-.469a.833.833 0 0 1-.612-.269l-1.041-1.13A.833.833 0 0 0 14.43 3h-4.27ZM5.498 7.167a.834.834 0 1 1 0 1.667.834.834 0 0 1 0-1.667Zm6.667 0a4.167 4.167 0 1 1 0 8.334 4.167 4.167 0 0 1 0-8.334Zm0 1.666a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z"/></svg>`,
};

const idDocument: RktIcon<'id-document'> = {
  name: 'id-document',
  data: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20"><path fill="#4A4A4A" d="M1.664 5v10c0 .917.75 1.667 1.667 1.667h13.333c.917 0 1.667-.75 1.667-1.666V5c0-.917-.75-1.667-1.667-1.667H3.331c-.921 0-1.667.746-1.667 1.667Zm5 1.667A1.666 1.666 0 1 1 6.665 10a1.666 1.666 0 0 1 0-3.333Zm-2.5 5.917c0-.832 1.666-1.25 2.5-1.25.834 0 2.5.418 2.5 1.25v.75h-5v-.75Zm11.667-3.417h-5V7.501h5v1.666Zm0 3.334h-5v-1.667h5v1.667Z"/></svg>`,
};

const attachmentImg: RktIcon<'attachment-img'> = {
  name: 'attachment-img',
  data: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20"><path fill="currentColor" d="M16.667 3.333H3.334c-.921 0-1.667.746-1.667 1.667v10c0 .92.746 1.666 1.667 1.666h13.333c.92 0 1.667-.745 1.667-1.666V5c0-.921-.746-1.667-1.667-1.667ZM8.334 6.666a.834.834 0 1 1 0 1.668.834.834 0 0 1 0-1.668Zm6.666 7.5H5.02a.416.416 0 0 1-.329-.672l2.075-2.668a.416.416 0 0 1 .65-.01l1.752 2.109 2.582-3.327a.416.416 0 0 1 .662.006l2.923 3.896a.417.417 0 0 1-.334.666Z"/></svg>`,
};

const flinks: RktIcon<'flinks'> = {
  name: 'flinks',
  data: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16">
  <path fill="currentColor" d="M11.302 7.429a17.691 17.691 0 0 0-1.16-1.503c.818-.947 1.524-1.77 1.538-1.785a.618.618 0 0 0 .137-.402.613.613 0 0 0-.147-.397.461.461 0 0 0-.346-.163c-.13 0-.254.06-.346.164-.014.017-.712.828-1.522 1.766-.248-.292-.504-.59-.757-.882.617-.705 1.183-1.342 1.55-1.751a.615.615 0 0 0 .147-.4.618.618 0 0 0-.14-.403.465.465 0 0 0-.346-.171.46.46 0 0 0-.35.16c-.068.075-.735.82-1.56 1.762-.824-.941-1.492-1.687-1.559-1.762a.493.493 0 0 0-.16-.121.436.436 0 0 0-.378.005.497.497 0 0 0-.16.126.583.583 0 0 0-.104.185.646.646 0 0 0 .005.436c.025.068.062.13.108.183.367.409.933 1.046 1.55 1.751-.253.293-.509.59-.757.881-.81-.937-1.507-1.748-1.521-1.764a.499.499 0 0 0-.16-.128.438.438 0 0 0-.38-.006.493.493 0 0 0-.161.123.58.58 0 0 0-.11.184.645.645 0 0 0 .107.624c.015.016.72.838 1.538 1.785-.41.476-.797.978-1.16 1.503A4.702 4.702 0 0 0 4 9.899c.004 1.219.425 2.387 1.173 3.249.748.862 1.761 1.348 2.819 1.352h.016c1.058-.004 2.071-.49 2.819-1.352.748-.862 1.17-2.03 1.173-3.25a4.694 4.694 0 0 0-.698-2.47ZM8 13.367c-.799-.003-1.564-.37-2.129-1.023-.564-.652-.882-1.535-.883-2.457V9.86a3.466 3.466 0 0 1 .516-1.772c.329-.473.68-.926 1.051-1.357l.744.857-.088.099c-1.018 1.14-1.044 2.098-1.044 2.202 0 .557.192 1.092.533 1.488.34.396.803.62 1.287.626h.027c.483-.005.946-.23 1.287-.625.34-.396.532-.93.533-1.488 0-.105-.025-1.064-1.044-2.204l-.088-.099.744-.856c.371.43.722.883 1.052 1.356.323.516.503 1.133.516 1.769v.032c-.001.922-.319 1.805-.884 2.458-.565.652-1.33 1.02-2.13 1.022Zm.1-4.869c.705.789.746 1.389.746 1.389.002.258-.086.507-.243.691a.795.795 0 0 1-.596.29.791.791 0 0 1-.6-.28 1.053 1.053 0 0 1-.252-.687c0-.007.036-.609.746-1.403l.1-.113.1.113ZM8 6.787l-.758-.873c.245-.29.502-.588.759-.884.257.296.513.594.758.883-.264.305-.522.603-.759.874Z"/>
  </svg>`,
};

export const iconsProvider: () => Provider = () => {
  return {
    provide: ENVIRONMENT_INITIALIZER,
    multi: true,
    useValue: () =>
      inject(RktIconRegistryService).registerIcons([
        bank,
        moveMoney,
        document,
        user,
        users,
        checkboxOn,
        checkboxOff,
        arrowLeft,
        arrowRight,
        chevronDown,
        chevronLeft,
        chevronRight,
        chevronUp,
        info,
        upload,
        check,
        times,
        close,
        passport,
        company,
        driverLicense,
        accounting,
        businessman,
        calendar,
        monitor,
        camera,
        beneficiary,
        identificationChecked,
        idDocument,
        attachmentImg,
        download,
        viewFile,
        plus,
        attachmentImg,
        flinks,
        card,
        schedule,
        editOutline,
        trashOutline,
      ]),
  };
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideEnvironmentNgxMask(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    importProvidersFrom(
      BrowserModule,
      ToastrModule.forRoot({
        preventDuplicates: true,
      }),
      FormlyModule.forRoot({
        types: [
          { name: 'formly-group', component: RktFormlyFieldGroupComponent },
          { name: 'input', component: RktFormInputComponent, wrappers: ['field-wrapper'] },
          { name: 'input-phone', component: RktFormInputPhoneComponent, wrappers: ['field-wrapper'] },
          { name: 'input-place', component: RktFormInputPlaceComponent, wrappers: ['field-wrapper'] },
          { name: 'input-password', component: RktFormInputPasswordComponent, wrappers: ['field-wrapper'] },
          { name: 'input-card', component: RktFormInputCardComponent, wrappers: ['field-wrapper'] },
          { name: 'input-secure', component: RktFormInputSecureComponent, wrappers: ['field-wrapper'] },
          { name: 'number', extends: 'input', defaultOptions: { props: { type: 'number' } } },
          { name: 'select', component: RktFormSelectComponent, wrappers: ['field-wrapper'] },
          { name: 'select-country', component: RktFormSelectCountryComponent, wrappers: ['field-wrapper'] },
          { name: 'select-state', component: RktFormSelectStateComponent, wrappers: ['field-wrapper'] },
          { name: 'select-multi', component: RktFormSelectMultiComponent, wrappers: ['field-wrapper'] },
          { name: 'textarea', component: RktFormTextareaComponent, wrappers: ['field-wrapper'] },
          { name: 'radio-group', component: RktFormRadioGroupComponent, wrappers: ['field-wrapper'] },
          { name: 'checkbox-group', component: RktFormCheckboxGroupComponent, wrappers: ['field-wrapper'] },
          { name: 'select-group', component: RktFormSelectGroupComponent, wrappers: ['field-wrapper'] },
          { name: 'repeat', component: RktFormRepeatComponent, wrappers: ['field-wrapper'] },
          { name: 'repeat-section', component: RktFormRepeatSectionComponent, wrappers: ['field-wrapper'] },
          { name: 'address-repeat-section', component: AddressRepeatSectionComponent, wrappers: ['field-wrapper'] },
          { name: 'datepicker', component: RktFormDatepickerComponent, wrappers: ['field-wrapper'] },
          { name: 'timepicker', component: RktFormTimepickerComponent, wrappers: ['field-wrapper'] },
        ],
        wrappers: [
          { name: 'field-wrapper', component: RktFormFieldWrapperComponent },
          { name: 'form-section', component: FormSectionWrapperComponent },
          { name: 'character-count', component: CharacterCountWrapperComponent },
        ],
        extensions: [{ name: 'typographic-quotes', extension: typographicQuotesExtension }],
        validationMessages: [
          { name: 'required', message: requiredValidationMessage },
          { name: 'minLength', message: minLengthValidationMessage },
          { name: 'maxLength', message: maxLengthValidationMessage },
          { name: 'pattern', message: patternValidationMessage },
        ],
        validators: [
          { name: 'isValidPhoneNumber', validation: phoneNumberValidator },
          { name: 'isValidCountryCode', validation: countryCodeValidator },
          { name: 'isValidStateCode', validation: stateCodeValidator },
          { name: 'isValidCardNumber', validation: cardNumberValidator },
        ],
      }),
    ),
    ValidUUIDGuard,
    Title,
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: APP_ENV_CONFIG, useFactory: getEnvironment },
    ...sentryProviders(),
    iconsProvider(),
    provideRouter(
      APP_ROUTES,
      withComponentInputBinding(),
      withHashLocation(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      // KOR-15102: a lazy route whose chunk a deploy removed fails here. This is the only surface
      // that can retry the import in place, by re-running the navigation.
      withNavigationErrorHandler((event) => {
        const stage = handleChunkNavigationError(event, inject(ChunkLoadRecoveryService));

        if (stage) {
          captureChunkLoadRecovery(stage, event.url);
        }
      }),
    ),
  ],
};
